import { knownNamesSoundAlike } from "@/src/lib/name-matching/pronunciation-exceptions";
import { isLetterVowel, readPronunciation, respellNameToken } from "@/src/lib/name-matching/pronunciation-rules";

const MAX_KEYS_PER_TOKEN = 64;
const LONG_VOWEL_SOUNDS: ReadonlyMap<string, string> = new Map([
  ["A", "AY"],
  ["E", "EE"],
  ["I", "IE"],
  ["O", "OH"],
  ["U", "OO"],
]);
const VOWEL_SOUNDS = new Set([
  ...LONG_VOWEL_SOUNDS.keys(),
  ...LONG_VOWEL_SOUNDS.values(),
  "AW",
  "OW",
  "OY",
  "IA",
  "EO",
  "E!",
  "@",
]);

type Phoneme = {
  sound: string;
  followsDoubledConsonant: boolean;
};

/**
 * Compare normalized name tokens, honoring known exceptions before spelling-to-sound rules.
 * Both tokens are folded first so an accent cannot bypass an exception (Iván/Yvonne).
 */
export function soundsAlike(left: string, right: string): boolean {
  const foldedLeft = foldNameToken(left);
  const foldedRight = foldNameToken(right);

  if (foldedLeft === foldedRight) return true;

  const knownMatch = knownNamesSoundAlike(foldedLeft, foldedRight);

  if (knownMatch !== null) return knownMatch;

  const rightKeys = new Set(pronunciationKeys(foldedRight));

  return pronunciationKeys(foldedLeft).some((key) => rightKeys.has(key));
}

// Keep this folding narrower than ASCII sanitization: unsupported letters must fail closed.
function foldNameToken(token: string): string {
  return token.normalize("NFD").replace(/\p{M}/gu, "").replace(/đ/g, "d").replace(/'/g, "");
}

/**
 * Approximate pronunciation keys for one lower-case name token. Vowels are retained to distinguish
 * names such as John/Joan and Carl/Carla. Unsupported spellings and excessive ambiguity return no keys.
 */
export function pronunciationKeys(token: string): string[] {
  const letters = foldNameToken(token);

  if (!/^[a-z]+$/.test(letters)) return [];

  const pronunciations = scanPronunciations(respellNameToken(letters));
  const keys = pronunciations.flatMap((phonemes) =>
    applyStress(phonemes).map((pronunciation) => pronunciation.map(({ sound }) => sound).join(" "))
  );

  return keys.length > MAX_KEYS_PER_TOKEN ? [] : [...new Set(keys)];
}

function scanPronunciations(word: string): Phoneme[][] {
  let pronunciations: Phoneme[][] = [[]];
  let index = 0;
  let followsDoubledConsonant = false;

  while (index < word.length) {
    const letter = word[index];

    // Doubled consonants sound once and keep the preceding vowel short (Ellie, Tammy).
    // The cc spelling has its own rule because it can represent two different sounds.
    if (letter === word[index + 1] && !isLetterVowel(letter) && letter !== "c") {
      index += 1;
      followsDoubledConsonant = true;
      continue;
    }

    const reading = readPronunciation(word, index, followsDoubledConsonant);

    if (!reading) return [];

    const alternatives = reading.alternatives.map((sounds) =>
      sounds.map((sound, soundIndex) => ({
        sound,
        followsDoubledConsonant: followsDoubledConsonant && soundIndex === 0,
      }))
    );

    // Check before expanding so an ambiguous spelling cannot create an unbounded candidate set.
    if (pronunciations.length * alternatives.length > MAX_KEYS_PER_TOKEN) return [];

    pronunciations = pronunciations.flatMap((prefix) => alternatives.map((suffix) => [...prefix, ...suffix]));
    followsDoubledConsonant = false;
    index += reading.spelling.length;
  }

  return pronunciations;
}

function isVowel(phoneme: Phoneme): boolean {
  return !phoneme.followsDoubledConsonant && VOWEL_SOUNDS.has(phoneme.sound);
}

// The first full vowel carries stress; a leading schwa is the unstressed Mc/Mac prefix.
function stressedVowelIndex(phonemes: Phoneme[]): number {
  return phonemes.findIndex((phoneme) => isVowel(phoneme) && phoneme.sound !== "@");
}

/** Preserve stressed and final vowels, then expand open syllables and the pin/pen nasal merger. */
function applyStress(phonemes: Phoneme[]): Phoneme[][] {
  const reduced = reduceUnstressedVowels(phonemes);

  return lengthenOpenFirstSyllable(reduced).flatMap(mergeNasalFirstVowel);
}

function reduceUnstressedVowels(phonemes: Phoneme[]): Phoneme[] {
  const stressedIndex = stressedVowelIndex(phonemes);
  const lastIndex = phonemes.length - 1;

  return phonemes.map((phoneme, index) =>
    index !== stressedIndex &&
    index !== lastIndex &&
    !phoneme.followsDoubledConsonant &&
    LONG_VOWEL_SOUNDS.has(phoneme.sound)
      ? { ...phoneme, sound: "@" }
      : phoneme
  );
}

function lengthenOpenFirstSyllable(phonemes: Phoneme[]): Phoneme[][] {
  const stressedIndex = stressedVowelIndex(phonemes);

  if (stressedIndex === -1) return [phonemes];

  const longVowel = LONG_VOWEL_SOUNDS.get(phonemes[stressedIndex].sound);
  const consonant = phonemes[stressedIndex + 1];
  const nextVowel = phonemes[stressedIndex + 2];
  const isOpenSyllable =
    consonant && nextVowel && !isVowel(consonant) && !consonant.followsDoubledConsonant && isVowel(nextVowel);

  if (!longVowel || !isOpenSyllable) return [phonemes];

  const lengthened = [...phonemes];

  lengthened[stressedIndex] = { ...phonemes[stressedIndex], sound: longVowel };

  return [phonemes, lengthened];
}

function mergeNasalFirstVowel(phonemes: Phoneme[]): Phoneme[][] {
  const stressedIndex = stressedVowelIndex(phonemes);

  if (stressedIndex === -1) return [phonemes];

  const vowel = phonemes[stressedIndex].sound;
  const nasal = phonemes[stressedIndex + 1];
  const afterNasal = phonemes[stressedIndex + 2];

  // Only a nasal cluster allows the merger (Quentin/Quinton). Open or doubled nasals keep
  // Gina/Jenna and Jenny/Ginny apart; pinyin ing and eng remain different finals (Ping/Peng).
  if (
    (vowel !== "E" && vowel !== "I") ||
    !nasal ||
    nasal.followsDoubledConsonant ||
    (nasal.sound !== "n" && nasal.sound !== "m") ||
    !afterNasal ||
    (nasal.sound === "n" && afterNasal.sound === "g") ||
    isVowel(afterNasal)
  )
    return [phonemes];

  const merged = [...phonemes];

  merged[stressedIndex] = { ...phonemes[stressedIndex], sound: vowel === "E" ? "I" : "E" };

  return [phonemes, merged];
}
